import { useMutation, useQueryClient } from '@tanstack/react-query';
import CarePeriodService from '@/services/CarePeriodService';
import type {
  WithdrawPatientInput, RequestWithdrawalInput, CancelWithdrawalRequestInput,
} from '@/services/CarePeriodService/endpoints';
import TaskService from '@/services/TaskService';
import type { TaskInstance } from '@/services/TaskService';
import { openWithdrawalRequest, isOpenWithdrawalRequest } from '@/logic/withdrawalRequest';
import { scheduleInvalidate } from '@/state/data/_shared/wf-race';
import { DASHBOARD_KEYS } from '@/state/data/dashboard/keys';
import { TASK_KEYS } from '@/state/data/task/keys';
import { CARE_PERIOD_KEYS } from './keys';

// Delayed invalidation, not immediate: WF67 writes `withdrawn` a couple of seconds
// after the ack, so an eager refetch reads the pre-withdrawal row. The dashboard key
// is included so the patient drops off the board without a manual refresh.
export function useWithdrawPatient() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, WithdrawPatientInput>({
    mutationFn: (vars) => CarePeriodService.withdrawPatient(vars),
    onSuccess: (_data, vars) => {
      scheduleInvalidate(qc, CARE_PERIOD_KEYS.byUser(vars.withingsUserId));
      scheduleInvalidate(qc, DASHBOARD_KEYS.all);
      scheduleInvalidate(qc, TASK_KEYS.all);
    },
  });
}

export const REQUEST_CONFIRM_POLL_MS = 2500;
export const REQUEST_CONFIRM_TIMEOUT_MS = 30000;

export interface WithdrawalRequestOutcome {
  confirmed: boolean;
  task: TaskInstance | null;
  rows: TaskInstance[];
}

export interface ConfirmWithdrawalRequestOpts {
  withingsUserId: string | number;
  carePeriodId: string | number;
  fetchRequests?: (uid: string | number) => Promise<TaskInstance[]>;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  pollMs?: number;
  timeoutMs?: number;
}

// The event is fire-and-forward, so the request counts as recorded only once an open one is read
// back. A request WF67 deduped counts too: it is the same open task.
export async function confirmWithdrawalRequested({
  withingsUserId,
  carePeriodId,
  fetchRequests = TaskService.getWithdrawalRequests,
  sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }),
  now = () => Date.now(),
  pollMs = REQUEST_CONFIRM_POLL_MS,
  timeoutMs = REQUEST_CONFIRM_TIMEOUT_MS,
}: ConfirmWithdrawalRequestOpts): Promise<WithdrawalRequestOutcome> {
  const deadline = now() + timeoutMs;
  const poll = async (): Promise<WithdrawalRequestOutcome> => {
    if (now() >= deadline) return { confirmed: false, task: null, rows: [] };
    await sleep(pollMs);
    // A transient read failure is not a verdict — keep polling until the deadline.
    const rows = await fetchRequests(withingsUserId).catch(() => null);
    const task = rows ? openWithdrawalRequest(rows, carePeriodId) : null;
    if (rows && task) return { confirmed: true, task, rows };
    return poll();
  };
  return poll();
}

function settleRequestRead(
  qc: ReturnType<typeof useQueryClient>,
  withingsUserId: string | number,
  outcome: { confirmed: boolean; rows: TaskInstance[] } | undefined,
) {
  if (outcome?.confirmed) {
    qc.setQueryData(TASK_KEYS.withdrawalRequests(withingsUserId), outcome.rows);
    qc.invalidateQueries({ queryKey: DASHBOARD_KEYS.all });
    // The task lists change too; the key just seeded is already fresh.
    qc.invalidateQueries({ queryKey: TASK_KEYS.all, predicate: (q) => q.queryKey[1] !== 'withdrawal-requests' });
  } else {
    scheduleInvalidate(qc, TASK_KEYS.all);
  }
}

// Resolves once the request is read back, or after the cap with confirmed:false; never rejects on
// a timeout, since WF67 may still land later.
export function useRequestWithdrawal() {
  const qc = useQueryClient();
  return useMutation<WithdrawalRequestOutcome, Error, RequestWithdrawalInput>({
    mutationFn: async (vars) => {
      await CarePeriodService.requestWithdrawal(vars);
      return confirmWithdrawalRequested({ withingsUserId: vars.withingsUserId, carePeriodId: vars.carePeriodId });
    },
    onSettled: (outcome, _e, vars) => settleRequestRead(qc, vars.withingsUserId, outcome),
  });
}

export interface CancelReadBack {
  confirmed: boolean;
  rows: TaskInstance[];
}

export interface ConfirmRequestClosedOpts {
  withingsUserId: string | number;
  taskId: string | number;
  fetchRequests?: (uid: string | number) => Promise<TaskInstance[]>;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  pollMs?: number;
  timeoutMs?: number;
}

// The inverse read-back: WF67 refuses some cancels without a word, so a cancel counts only once the row reads closed.
export async function confirmWithdrawalRequestClosed({
  withingsUserId,
  taskId,
  fetchRequests = TaskService.getWithdrawalRequests,
  sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }),
  now = () => Date.now(),
  pollMs = REQUEST_CONFIRM_POLL_MS,
  timeoutMs = REQUEST_CONFIRM_TIMEOUT_MS,
}: ConfirmRequestClosedOpts): Promise<CancelReadBack> {
  const deadline = now() + timeoutMs;
  const poll = async (): Promise<CancelReadBack> => {
    if (now() >= deadline) return { confirmed: false, rows: [] };
    await sleep(pollMs);
    const rows = await fetchRequests(withingsUserId).catch(() => null);
    const row = rows?.find((t) => String(t.task_id) === String(taskId));
    if (rows && row && !isOpenWithdrawalRequest(row)) return { confirmed: true, rows };
    return poll();
  };
  return poll();
}

// No optimistic patch: the cancel is async and may be refused, so the window shows only what it reads back.
export function useCancelWithdrawalRequest() {
  const qc = useQueryClient();
  return useMutation<CancelReadBack, Error, CancelWithdrawalRequestInput & { taskId: string | number }>({
    mutationFn: async ({ taskId, ...vars }) => {
      await CarePeriodService.cancelWithdrawalRequest(vars);
      return confirmWithdrawalRequestClosed({ withingsUserId: vars.withingsUserId, taskId });
    },
    onSettled: (outcome, _e, vars) => {
      settleRequestRead(qc, vars.withingsUserId, outcome);
      if (!outcome?.confirmed) qc.invalidateQueries({ queryKey: TASK_KEYS.withdrawalRequests(vars.withingsUserId) });
    },
  });
}
